import { useCallback, useRef, type MouseEvent, type ReactNode } from "react";
import { hasAsyncPlaceholders } from "../../shared/template";
import { useAsyncHrefNavigation } from "../hooks/useAsyncHrefNavigation";
import { isPlainLeftClick } from "../utils/isPlainLeftClick";

interface AsyncLinkInterceptorProps {
  children: ReactNode;
}

const findAnchorAncestor = (start: EventTarget | null, container: HTMLElement | null): HTMLAnchorElement | null => {
  if (!(start instanceof Node) || !container) return null;
  let node: Node | null = start;
  while (node && node !== container) {
    if (node instanceof HTMLAnchorElement) return node;
    node = node.parentNode;
  }
  return null;
};

/**
 * Wraps user-authored rich-text/HTML content and intercepts clicks on anchors
 * whose href contains async template placeholders (e.g. {{season.token}}).
 * Resolves the URL via interpolateTextAsync at click time and opens it.
 *
 * Clicks on plain links pass through to the browser's native handling
 * (preserving middle-click, Cmd-click, copy-link-address).
 */
export const AsyncLinkInterceptor = ({ children }: AsyncLinkInterceptorProps) => {
  const openAsyncHref = useAsyncHrefNavigation();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!isPlainLeftClick(event)) return;

      const anchor = findAnchorAncestor(event.target, containerRef.current);
      if (!anchor) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref || !hasAsyncPlaceholders(rawHref)) return;

      event.preventDefault();
      event.stopPropagation();

      void openAsyncHref(rawHref);
    },
    [openAsyncHref]
  );

  return (
    <div ref={containerRef} onClick={handleClick}>
      {children}
    </div>
  );
};
